import Rating from 'components/rating/Rating';
import { getRemainingBalance, round } from 'modules/common';
import { userTransactionsQuery } from 'modules/graphqlQueries';
import request from 'modules/request';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { toast } from 'react-toastify';

import { UserType } from '../../types/UserTypes';
import DefaultLogo from '../defaultLogo/DefaultLogo';
import {
  CompanyName,
  Detail,
  HeroChildren,
  Image,
  Info,
  LeftColumn,
  PendingInvoices,
  RightColumn,
  SupplierInfo,
  Text,
  Title,
  Value,
} from './HeroUser.styles';
import RemainingServicesBalance from './remainingServicesBalance/RemainingServicesBalance';

interface HeroSupplierProps {
  isShippingCompany?: boolean;
  user: UserType;
  isPublic?: boolean;
}

const getRightColumn = (
  user: UserType,
  balance?: number
): JSX.Element | null => {
  let result = null;
  if (user.status === 'APPROVED' && !user.isTroubleshootingEnabled) {
    if (!user.bundlesEnabled) {
      result = <RemainingServicesBalance balance={balance} />;
    } else {
      result = (
        <RemainingServicesBalance remainingServices={user.remainingServices} />
      );
    }
  }
  return result;
};

const HeroSupplier = ({
  user,
  isShippingCompany = false,
  isPublic = false,
}: HeroSupplierProps): JSX.Element => {
  const { asPath } = useRouter();
  useEffect(() => {
    if (
      !isPublic &&
      (!sessionStorage.getItem('userBalance') || asPath === '/transactions')
    ) {
      const loadUserBalance = async () => {
        const response = await request(`
        {
          transactions(limit: 0, offset: 0) {
            ${userTransactionsQuery}
          }
        }
      `);

        if (response.errors) {
          toast.error(response.errors[0].message);
        } else {
          sessionStorage.setItem(
            'userBalance',
            getRemainingBalance(response.data?.transactions || []).toString()
          );
        }
      };

      loadUserBalance();
    }
  }, [isPublic, asPath]);

  return (
    <HeroChildren>
      <LeftColumn>
        {user?.hasPendingInvoices ? (
          <PendingInvoices>
            WARNING: You have pending invoices, please contact us at
            info@bid2board.com to settle them!
          </PendingInvoices>
        ) : (
          <>
            <div>
              {user?.logo ? (
                <Image src={user.logo} alt={`${user.companyName} logo`} />
              ) : (
                <DefaultLogo size="xxl" />
              )}
            </div>
            <Info>
              <SupplierInfo>
                <CompanyName
                  // The company name is the page heading on public profiles.
                  as={isPublic ? 'h1' : 'div'}
                  isSupplier={Boolean(user?.type === 'SUPPLIER')}
                >
                  {user.companyName ? user.companyName : ''}
                </CompanyName>
                <Rating
                  rating={user?.rating}
                  ratingCount={user?.ratingCount}
                  size="md"
                />
              </SupplierInfo>
              <Detail>
                <Title>Nationality:</Title>
                <Value>{user.nationality ? user.nationality : '-'}</Value>
              </Detail>
              <Detail>
                <Title>Staff:</Title>
                <Value>{user.staff ? user.staff : '-'}</Value>
              </Detail>
              <Detail>
                <Title>Port location:</Title>
                {user.ports.length !== 0 ? (
                  user.ports.map((port, index) => (
                    <Value key={port.name}>
                      <Text>{port.name}</Text>
                      {index + 1 < user.ports.length && <span>, &nbsp;</span>}
                    </Value>
                  ))
                ) : (
                  <Value>-</Value>
                )}
              </Detail>
            </Info>
          </>
        )}
      </LeftColumn>
      {!isPublic && (
        <RightColumn>
          {!isShippingCompany &&
            getRightColumn(
              user,
              round(parseFloat(sessionStorage.getItem('userBalance') || '0'), 2)
            )}
        </RightColumn>
      )}
    </HeroChildren>
  );
};

export default HeroSupplier;
